import React, { useId, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button, Field, inputClass } from '../ds';
import { ErrorNote } from '../workspace/ErrorNote';
import { useAuth } from '../../context/AuthContext';
import { apiFetch, ApiError } from '../../utils/api';
import { track } from '../../utils/analytics';
import { Page } from './Page';
import { LogoMark } from './Logo';

const MIN_PASSWORD_CHARS = 8;

/** A centered surface card with the logo mark, a title and a line under it */
export const AuthCard = ({ title, subtitle, children }) => (
  <Page className="flex items-start sm:items-center justify-center px-space-4 py-space-10">
    <div className="w-full max-w-[460px] flex flex-col gap-space-6 p-space-6 sm:p-space-8 rounded-radius-xl bg-surface">
      <div className="flex flex-col gap-space-3">
        <LogoMark className="w-12 h-12" />
        <h1 className="m-0 font-display text-title-2 text-ink">{title}</h1>
        {subtitle && <p className="m-0 text-body text-ink-soft">{subtitle}</p>}
      </div>
      {children}
    </div>
  </Page>
);

const TextField = ({ label, hint, ...input }) => {
  const id = useId();
  return (
    <Field id={id} label={label} hint={hint}>
      <input id={id} required className={inputClass} {...input} />
    </Field>
  );
};

const textLink = 'font-bold text-amber hover:text-amber-hover underline-offset-2 hover:underline';

// Server errors carry the backend's message; anything else means we never got a response
const authErrorMessage = (err) => (
  err instanceof ApiError ? err.message : 'We couldn\'t reach the server. Please try again.'
);

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await apiFetch('/auth/login', { method: 'POST', json: { email, password } });
      login(data.access_token, data.user);
      track('login', { method: 'email' });
      navigate('/projects');
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard title="Log in" subtitle="Welcome back. Your projects are waiting.">
      <form onSubmit={handleSubmit} className="flex flex-col gap-space-5">
        {error && <ErrorNote>{error}</ErrorNote>}
        <TextField label="Email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <TextField label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        <Button type="submit" disabled={loading} className="w-full">{loading ? 'Logging in…' : 'Log in'}</Button>
        <p className="m-0 text-small text-ink-soft">
          New here? <Link to="/signup" className={textLink}>Sign up free</Link>
        </p>
      </form>
    </AuthCard>
  );
};

export const SignUpPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError("The passwords don't match.");
      return;
    }
    if (password.length < MIN_PASSWORD_CHARS) {
      setError(`Your password needs at least ${MIN_PASSWORD_CHARS} characters.`);
      return;
    }
    setLoading(true);
    try {
      const data = await apiFetch('/auth/signup', { method: 'POST', json: { email, password } });
      login(data.access_token, data.user); // signed in straight away
      track('sign_up', { method: 'email' });
      navigate('/verify-pending');
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard title="Create your account" subtitle="10 free analyses a day. No card needed.">
      <form onSubmit={handleSubmit} className="flex flex-col gap-space-5">
        {error && <ErrorNote>{error}</ErrorNote>}
        <TextField label="Email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <TextField label="Password" hint={`At least ${MIN_PASSWORD_CHARS} characters`} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        <TextField label="Confirm password" type="password" autoComplete="new-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
        <Button type="submit" disabled={loading} className="w-full">{loading ? 'Creating your account…' : 'Create account'}</Button>
        <p className="m-0 text-small text-ink-soft">
          Already have an account? <Link to="/login" className={textLink}>Log in</Link>
        </p>
      </form>
    </AuthCard>
  );
};
